import { axiosInstance } from './axios';

// Types for invoice regeneration
export interface RegenerationRequest {
  regenerationType: 'Manual' | 'BugFix' | 'LayoutUpdate' | 'DataCorrection' | 'Compliance';
  reason?: string;
  preserveOriginal?: boolean;
  notifyCustomer?: boolean;
}

export interface EntityRegenerationInfo {
  entityId: number;
  entityType: 'Sale' | 'SaleReturn' | 'SaleOrder' | 'Quotation' | 'Purchase';
  specificReason?: string;
}

export interface BulkRegenerationRequest {
  entities: EntityRegenerationInfo[];
  baseRequest: RegenerationRequest;
  stopOnFirstError?: boolean;
}

export interface DocumentVersion {
  id: number;
  entityId: number;
  entityType: string;
  documentUrl: string;
  version: number;
  isActive: boolean;
  regenerationType?: string;
  regenerationReason?: string;
  regeneratedBy?: string;
  createdAt: string;
  replacedAt?: string;
}

export interface RegenerationResult {
  success: boolean;
  errorMessage?: string;
  newVersion?: DocumentVersion;
  previousVersion?: DocumentVersion;
  auditId: number;
  processingTime: string;
}

export interface BulkRegenerationResult {
  totalRequested: number;
  successCount: number;
  failureCount: number;
  results: RegenerationResult[];
  errors: string[];
  totalProcessingTime: string;
}

// Invoice Regeneration API Service
export const invoiceRegenerationService = {
  // Regenerate single sale invoice
  regenerateSaleInvoice: async (saleId: number, request: RegenerationRequest): Promise<RegenerationResult> => {
    const response = await axiosInstance.post(`/invoiceregeneration/sales/${saleId}/regenerate`, request);
    return response.data;
  },

  // Regenerate sale order (proforma invoice)
  regenerateSaleOrder: async (saleOrderId: number, request: RegenerationRequest): Promise<RegenerationResult> => {
    const response = await axiosInstance.post(`/invoiceregeneration/sale-orders/${saleOrderId}/regenerate`, request);
    return response.data;
  },

  // Regenerate sale return credit note
  regenerateSaleReturn: async (saleReturnId: number, request: RegenerationRequest): Promise<RegenerationResult> => {
    const response = await axiosInstance.post(`/invoiceregeneration/sale-returns/${saleReturnId}/regenerate`, request);
    return response.data;
  },

  // Regenerate quotation document
  regenerateQuotation: async (quotationId: number, request: RegenerationRequest): Promise<RegenerationResult> => {
    const response = await axiosInstance.post(`/invoiceregeneration/quotations/${quotationId}/regenerate`, request);
    return response.data;
  },

  // Regenerate purchase invoice
  regeneratePurchaseInvoice: async (purchaseId: number, request: RegenerationRequest): Promise<RegenerationResult> => {
    const response = await axiosInstance.post(`/invoiceregeneration/purchases/${purchaseId}/regenerate`, request);
    return response.data;
  },

  // Bulk regenerate multiple documents
  bulkRegenerate: async (request: BulkRegenerationRequest): Promise<BulkRegenerationResult> => {
    const response = await axiosInstance.post('/invoiceregeneration/bulk-regenerate', request);
    return response.data;
  },

  // Get current document version
  getCurrentVersion: async (entityType: string, entityId: number): Promise<DocumentVersion | null> => {
    try {
      const response = await axiosInstance.get(`/invoiceregeneration/${entityType}/${entityId}/current-version`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  // Get version history
  getVersionHistory: async (entityType: string, entityId: number): Promise<DocumentVersion[]> => {
    const response = await axiosInstance.get(`/invoiceregeneration/${entityType}/${entityId}/version-history`);
    return response.data;
  },

  // Revert to a specific version
  revertToVersion: async (
    entityType: string,
    entityId: number,
    version: number,
    request: RegenerationRequest
  ): Promise<RegenerationResult> => {
    const response = await axiosInstance.post(
      `/invoiceregeneration/${entityType}/${entityId}/revert/${version}`,
      request
    );
    return response.data;
  }
};

// Helper function to get regeneration type display name
export const getRegenerationTypeDisplayName = (type: string): string => {
  switch (type) {
    case 'Manual':
      return 'Manual Regeneration';
    case 'BugFix':
      return 'Bug Fix';
    case 'LayoutUpdate':
      return 'Layout Update';
    case 'DataCorrection':
      return 'Data Correction';
    case 'Compliance':
      return 'Compliance';
    default:
      return type;
  }
};

// Helper function to get regeneration type color
export const getRegenerationTypeColor = (type: string): string => {
  switch (type) {
    case 'Manual':
      return 'blue';
    case 'BugFix':
      return 'red';
    case 'LayoutUpdate':
      return 'green';
    case 'DataCorrection':
      return 'orange';
    case 'Compliance':
      return 'purple';
    default:
      return 'default';
  }
};
